import React, { useState } from 'react';
import {
  Title,
  Card,
  Stack,
  Group,
  Button,
  Text,
  ActionIcon,
  Modal,
  TextInput,
  Loader,
  Select,
} from '@mantine/core';
import { PageContainer } from '../../components/ui/PageContainer';
import { IconEdit, IconTrash, IconPlus } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';
import { createEnterKeyHandler } from '../../utils/enterKeyHandler';
import { useTranslation } from 'react-i18next';
import { Department } from '../../types';
import { useUsersDirectory } from '../../hooks/useUserApi';
import { usePermission } from '../../hooks/usePermission';

const nameOf = (list: { _id: string; name: string }[], id: string) =>
  list.find((x) => x._id === id)?.name ?? '—';

export const Departments: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editDepartment, setEditDepartment] = useState<Department | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [head, setHead] = useState<string | null>(null);
  const [parent, setParent] = useState<string | null>(null);
  // Руководитель — из справочника активных коллег: он открыт и тем, кто не управляет людьми.
  const { data: people = [] } = useUsersDirectory();
  // Руководитель — ребро «руководит»: сервер меняет его только держателю people.manage.
  const isHeadEditable = usePermission('people.manage');
  // Неактивный руководитель — с пометкой, а не «—»: в справочнике активных его нет.
  const headLabel = (h: NonNullable<Department['head']>) =>
    h.active === false ? `${h.name} (${t('departments.inactiveHead')})` : h.name;
  const currentHead = editDepartment?.head;
  const headOptions = [
    ...people.map((p) => ({ value: p._id, label: p.name })),
    ...(currentHead && !people.some((p) => p._id === currentHead._id)
      ? [{ value: currentHead._id, label: headLabel(currentHead) }]
      : []),
  ];

  // Получение департаментов с бэкенда
  const { data: departments = [], isLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => api('/departments'),
    staleTime: 5 * 60 * 1000,
  });

  // Мутация создания
  const createDepartmentMutation = useMutation({
    mutationFn: async (data: { name: string; head?: string; parent?: string }) =>
      api('/departments', { method: 'POST', body: data }),
    onSuccess: () => {
      showToast({
        title: t('departments.added'),
        message: t('departments.addedMsg'),
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setModalOpen(false);
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('departments.addError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  // Мутация редактирования
  const editDepartmentMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, string | null> }) =>
      api(`/departments/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      showToast({
        title: t('departments.updated'),
        message: t('departments.updatedMsg'),
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setModalOpen(false);
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('departments.editError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  // Мутация удаления
  const deleteDepartmentMutation = useMutation({
    mutationFn: async (_id: string) => api(`/departments/${_id}`, { method: 'DELETE' }),
    onSuccess: () => {
      showToast({
        title: t('departments.deleted'),
        message: t('departments.deletedMsg'),
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('departments.deleteError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const handleAdd = () => {
    setEditDepartment(null);
    setInputValue('');
    setHead(null);
    setParent(null);
    setModalOpen(true);
  };

  const handleEdit = (department: Department) => {
    setEditDepartment(department);
    setInputValue(department.name);
    setHead(department.head?._id ?? null);
    setParent(department.parent ?? null);
    setModalOpen(true);
  };

  const handleDeleteDepartment = (department: Department) => {
    modals.openConfirmModal({
      title: t('departments.deleteConfirmTitle'),
      centered: true,
      children: (
        <Text
          size="sm"
          dangerouslySetInnerHTML={{
            __html: t('departments.deleteConfirmText', { name: department.name }),
          }}
        />
      ),
      labels: { confirm: t('common.delete'), cancel: t('departments.cancel') },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => {
        deleteDepartmentMutation.mutate(department._id);
      },
    });
  };

  const handleSave = () => {
    if (inputValue.trim() === '') return;

    const name = inputValue.trim();
    if (editDepartment) {
      // Руководителя и родителя шлём, только если их поменяли: неизменного (в том числе уже
      // неактивного) руководителя сервер не перепроверяет, а менять его может не каждый.
      const body: Record<string, string | null> = { name };
      if (head !== (editDepartment.head?._id ?? null)) body.head = head;
      if (parent !== (editDepartment.parent ?? null)) body.parent = parent;
      editDepartmentMutation.mutate({ id: editDepartment._id, body });
    } else {
      createDepartmentMutation.mutate({
        name,
        ...(head ? { head } : {}),
        ...(parent ? { parent } : {}),
      });
    }
  };

  return (
    <PageContainer>
      <Group justify="space-between" mb={{ base: 'md', sm: 'xl' }}>
        <Title order={2}>{t('departments.title')}</Title>
        <Button variant="light" leftSection={<IconPlus size={16} />} onClick={handleAdd}>
          {t('departments.add')}
        </Button>
      </Group>

      <Stack gap="md" style={{ maxWidth: 600 }}>
        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : departments.length === 0 ? (
          <Text ta="center" py="xl">
            {t('departments.empty')}
          </Text>
        ) : (
          departments.map((department: Department) => (
            <Card key={department._id} shadow="sm" padding="md" radius="md" withBorder>
              <Group justify="space-between">
                <Stack gap={2} style={{ flex: 1 }}>
                  <Text size="md">{department.name}</Text>
                  {department.parent && (
                    <Text size="xs" c="dimmed">
                      {t('departments.parent')}: {nameOf(departments, department.parent)}
                    </Text>
                  )}
                  {department.head && (
                    <Text size="xs" c="dimmed">
                      {t('departments.head')}: {headLabel(department.head)}
                    </Text>
                  )}
                </Stack>
                <Group gap="xs">
                  <ActionIcon variant="light" color="teal" onClick={() => handleEdit(department)}>
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="light"
                    color="red"
                    onClick={() => handleDeleteDepartment(department)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Group>
            </Card>
          ))
        )}
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editDepartment ? t('departments.edit') : t('departments.add')}
        size="md"
      >
        <Stack>
          <TextInput
            label={t('departments.name')}
            placeholder={t('departments.placeholder')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            required
            autoFocus
            onKeyDown={createEnterKeyHandler({ onSubmit: handleSave })}
          />

          <Select
            label={t('departments.head')}
            description={
              isHeadEditable ? t('departments.headDescription') : t('departments.headManagedByPeople')
            }
            placeholder={t('departments.headPlaceholder')}
            data={headOptions}
            value={head}
            onChange={setHead}
            disabled={!isHeadEditable}
            searchable
            clearable
          />

          <Select
            label={t('departments.parent')}
            placeholder={t('departments.parentPlaceholder')}
            // Себя в родители не предлагаем; цикл через потомков отклонит сервер.
            data={departments
              .filter((d: Department) => d._id !== editDepartment?._id)
              .map((d: Department) => ({ value: d._id, label: d.name }))}
            value={parent}
            onChange={setParent}
            searchable
            clearable
          />

          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setModalOpen(false)} color="gray">
              {t('departments.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              color="teal"
              loading={createDepartmentMutation.isPending || editDepartmentMutation.isPending}
            >
              {editDepartment ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageContainer>
  );
};
